import { Link } from 'react-router-dom'
import { useAuth } from '@/auth/useAuth'
import type { NodeDetail } from '@/types'
import { formatKstDateTime } from '@/utils/datetime'
import {
  guardianAlphaConfigPath,
  guardianAlphaRestartResultPath,
  manualOperatingTargetPath,
} from '@/utils/navigation'
import guardianStyles from './GuardianAlphaStatusPanel.module.css'
import styles from './nodeDetail.module.css'
import { useGuardianAlphaRestart } from './useGuardianAlphaRestart'


interface Props {
  node: NodeDetail
  manualOperatingEnabled?: boolean
}

const MANUAL_OPERATING_ENABLED =
  import.meta.env.VITE_MANUAL_OPERATING_ENABLED === 'true'

function statusClassName(status: string): string {
  if (status === 'succeeded') return guardianStyles.statusSucceeded
  if (status === 'failed') return guardianStyles.statusFailed
  if (status === 'running' || status === 'applying') return guardianStyles.statusActive
  return guardianStyles.statusNeutral
}

export function GuardianAlphaStatusPanel({
  node,
  manualOperatingEnabled = MANUAL_OPERATING_ENABLED,
}: Props) {
  const { hasPermission } = useAuth()
  const canWriteConfig = hasPermission('guardian-alpha-config:write')
  const canViewConfig = hasPermission('config:read')
  const configHref = guardianAlphaConfigPath(node.id)
  const restartResultHref = guardianAlphaRestartResultPath(node.id)
  const {
    service,
    missing,
    loading,
    restarting,
    error,
    restartDisabledReason,
    restart,
  } = useGuardianAlphaRestart(node)

  if (loading) {
    return (
      <div className={styles.card}>
        <div className={styles.cardTitle}>GuardianAlpha</div>
        <span className={guardianStyles.loading}>Loading...</span>
      </div>
    )
  }

  if (!service && error) {
    return (
      <div className={styles.card}>
        <div className={styles.basicInfoHeader}>
          <div className={styles.cardTitle}>GuardianAlpha</div>
          <div className={guardianStyles.headerActions}>
            <Link to={restartResultHref} className={styles.secondaryButton}>
              최근 재시작 결과
            </Link>
            {canViewConfig && (
              <Link to={configHref} className={styles.secondaryButton}>
                {canWriteConfig ? 'Edit GuardianAlpha config' : 'View GuardianAlpha config'}
              </Link>
            )}
          </div>
        </div>
        <div className={guardianStyles.error} role="alert">{error}</div>
      </div>
    )
  }

  if (missing) {
    return (
      <div className={styles.card}>
        <div className={styles.basicInfoHeader}>
          <div className={styles.cardTitle}>GuardianAlpha</div>
          <div className={guardianStyles.headerActions}>
            <Link to={restartResultHref} className={styles.secondaryButton}>
              최근 재시작 결과
            </Link>
            {canViewConfig && (
              <Link
                to={configHref}
                className={styles.secondaryButton}
              >
                {canWriteConfig ? 'Register config' : 'View config'}
              </Link>
            )}
          </div>
        </div>
        <p className={guardianStyles.missingMessage}>
          GuardianAlpha 관리 대상으로 등록되지 않은 물리 노드입니다.
        </p>
      </div>
    )
  }

  if (!service) return null

  return (
    <div className={styles.card}>
      <div className={styles.basicInfoHeader}>
        <div className={styles.cardTitle}>GuardianAlpha</div>
        <div className={guardianStyles.headerActions}>
          <Link to={restartResultHref} className={styles.secondaryButton}>
            최근 재시작 결과
          </Link>
          {canViewConfig && (
            <Link
              to={configHref}
              className={styles.secondaryButton}
            >
              {canWriteConfig ? 'Edit GuardianAlpha config' : 'View GuardianAlpha config'}
            </Link>
          )}
          <button
            type="button"
            className={styles.primaryButton}
            onClick={restart}
            disabled={Boolean(restartDisabledReason)}
            title={restartDisabledReason ?? undefined}
          >
            {service.last_restart_status === 'running' || restarting
              ? 'Restarting...'
              : 'Restart GuardianAlpha'}
          </button>
        </div>
      </div>

      <div className={guardianStyles.statusSummary}>
        <strong className={statusClassName(service.last_restart_status)}>
          Restart: {service.last_restart_status}
        </strong>
        <span>
          Last completed: {formatKstDateTime(service.last_restarted_at, '—')}
        </span>
        <span>Registered targets: {service.guardian_nodes.length}</span>
      </div>

      {service.last_restart_status === 'succeeded' && (
        <div className={guardianStyles.success}>
          등록된 GuardianNode {service.guardian_nodes.length}개 프로세스 기동 확인 성공
        </div>
      )}
      {service.last_restart_error && (
        <div className={guardianStyles.error}>{service.last_restart_error}</div>
      )}
      {error && <div className={guardianStyles.error}>{error}</div>}
      {restartDisabledReason && service.last_restart_status !== 'running' && (
        <div className={guardianStyles.notice}>{restartDisabledReason}</div>
      )}

      <div className={guardianStyles.targetList}>
        {service.guardian_nodes.map((item) => (
          <div key={item.id} className={guardianStyles.target}>
            <div>
              <strong>{item.display_name}</strong>
              <div className={guardianStyles.targetPath}>
                {item.working_directory} · {item.process_entrypoint}
              </div>
            </div>
            <div className={guardianStyles.targetActions}>
              {manualOperatingEnabled && canViewConfig && (
                <Link
                  to={manualOperatingTargetPath(item.id)}
                  className={guardianStyles.targetLink}
                  aria-label={`Open ${item.display_name} Manual Operating`}
                >
                  Manual Operating
                </Link>
              )}
              <span
                className={`${guardianStyles.targetStatus} ${statusClassName(item.last_apply_status)}`}
              >
                config {item.last_apply_status}
              </span>
            </div>
          </div>
        ))}
      </div>
      <p className={guardianStyles.footnote}>
        성공 판정은 등록된 main process의 UID/cwd/entrypoint/count와 5초 유지 확인만 의미합니다.
      </p>
    </div>
  )
}
